// ─────────────────────────────────────────────────────────────────────────────
// Job platform configuration — country-aware
// Each entry: display name + URL generator that takes the job title string
// ─────────────────────────────────────────────────────────────────────────────

export interface PlatformLink {
  name: string;
  getUrl: (title: string) => string;
}

const enc = encodeURIComponent;
const slug = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// ── Shared platform builders ──────────────────────────────────────────────────

const linkedIn = (country: string): PlatformLink => ({
  name: "LinkedIn",
  getUrl: (t) =>
    `https://www.linkedin.com/jobs/search/?keywords=${enc(t)}&location=${enc(country)}`,
});

// indeed.com uses country-code subdomains (US is the exception: www.indeed.com)
const indeed = (subdomain: string): PlatformLink => ({
  name: "Indeed",
  getUrl: (t) =>
    subdomain === "www"
      ? `https://www.indeed.com/jobs?q=${enc(t)}`
      : `https://${subdomain}.indeed.com/jobs?q=${enc(t)}`,
});

const glassdoor: PlatformLink = {
  name: "Glassdoor",
  getUrl: (t) =>
    `https://www.glassdoor.com/Job/jobs.htm?sc.keyword=${enc(t)}`,
};

// ── GCC / Middle East ─────────────────────────────────────────────────────────

// Bayt country slugs
const bayt = (countrySlug: string): PlatformLink => ({
  name: "Bayt.com",
  getUrl: (t) =>
    `https://www.bayt.com/en/${countrySlug}/jobs/?q=${enc(t)}`,
});

const gulfTalent = (country: string): PlatformLink => ({
  name: "GulfTalent",
  getUrl: (t) =>
    `https://www.gulftalent.com/jobs/search?keywords=${enc(t)}&locations_str=${enc(country)}`,
});

const naukriGulf: PlatformLink = {
  name: "Naukri Gulf",
  getUrl: (t) => `https://www.naukrigulf.com/${slug(t)}-jobs`,
};

const akhtaboot: PlatformLink = {
  name: "Akhtaboot",
  getUrl: (t) => `https://www.akhtaboot.com/jobs/?q=${enc(t)}`,
};

const wuzzuf: PlatformLink = {
  name: "Wuzzuf",
  getUrl: (t) => `https://wuzzuf.net/search/jobs/?q=${enc(t)}`,
};

const kariyer: PlatformLink = {
  name: "Kariyer.net",
  getUrl: (t) => `https://www.kariyer.net/is-ilanlari?pozisyon=${enc(t)}`,
};

// ── South Asia ────────────────────────────────────────────────────────────────

const naukri: PlatformLink = {
  name: "Naukri",
  getUrl: (t) => `https://www.naukri.com/${slug(t)}-jobs`,
};

const shine: PlatformLink = {
  name: "Shine.com",
  getUrl: (t) => `https://www.shine.com/job-search/?q=${enc(t)}`,
};

const rozee: PlatformLink = {
  name: "Rozee.pk",
  getUrl: (t) => `https://www.rozee.pk/jobs?q=${enc(t)}`,
};

const bdjobs: PlatformLink = {
  name: "BDjobs",
  getUrl: (t) => `https://www.bdjobs.com/jobssearch.asp?job_descr=${enc(t)}`,
};

// ── Southeast Asia ────────────────────────────────────────────────────────────

const jobStreet = (tld: string): PlatformLink => ({
  name: "JobStreet",
  getUrl: (t) => `https://www.jobstreet.${tld}/jobs?q=${enc(t)}`,
});

const myCareersFuture: PlatformLink = {
  name: "MyCareersFuture",
  getUrl: (t) => `https://www.mycareersfuture.gov.sg/search?search=${enc(t)}`,
};

const jobsDB = (tld: string): PlatformLink => ({
  name: "JobsDB",
  getUrl: (t) => `https://hk.jobsdb.com/jobs-in-${slug(t)}`, // HK/HK
});

const glints: PlatformLink = {
  name: "Glints",
  getUrl: (t) => `https://glints.com/id/opportunities/jobs/explore?keyword=${enc(t)}`,
};

const vietnamWorks: PlatformLink = {
  name: "VietnamWorks",
  getUrl: (t) => `https://www.vietnamworks.com/viec-lam?q=${enc(t)}`,
};

// ── East Asia ─────────────────────────────────────────────────────────────────

const rikunabi: PlatformLink = {
  name: "Rikunabi NEXT",
  getUrl: (t) => `https://next.rikunabi.com/rnc/docs/cp_s00890.jsp?k=${enc(t)}`,
};

const saramin: PlatformLink = {
  name: "Saramin",
  getUrl: (t) => `https://www.saramin.co.kr/zf_user/search?searchword=${enc(t)}`,
};

const zhaopin: PlatformLink = {
  name: "Zhaopin",
  getUrl: (t) => `https://www.zhaopin.com/jobs?kw=${enc(t)}`,
};

const boss: PlatformLink = {
  name: "BOSS直聘",
  getUrl: (t) => `https://www.zhipin.com/jobs/geek/recommend/?query=${enc(t)}`,
};

const job104: PlatformLink = {
  name: "104人力銀行",
  getUrl: (t) => `https://www.104.com.tw/jobs/search/?keyword=${enc(t)}`,
};

// ── Oceania ───────────────────────────────────────────────────────────────────

const seek = (tld: string): PlatformLink => ({
  name: "Seek",
  getUrl: (t) => `https://www.seek.${tld}/jobs?keywords=${enc(t)}`,
});

const tradeMe: PlatformLink = {
  name: "Trade Me Jobs",
  getUrl: (t) => `https://www.trademe.co.nz/a/jobs?search_string=${enc(t)}`,
};

// ── North America ─────────────────────────────────────────────────────────────

const zipRecruiter: PlatformLink = {
  name: "ZipRecruiter",
  getUrl: (t) => `https://www.ziprecruiter.com/Jobs/${slug(t)}`,
};

const monster = (subdomain: string): PlatformLink => ({
  name: "Monster",
  getUrl: (t) =>
    `https://www.monster.${subdomain}/jobs/search?q=${enc(t)}`,
});

const jobBank: PlatformLink = {
  name: "Job Bank Canada",
  getUrl: (t) => `https://www.jobbank.gc.ca/jobsearch/jobsearch?searchstring=${enc(t)}`,
};

// ── UK & Ireland ──────────────────────────────────────────────────────────────

const reed: PlatformLink = {
  name: "Reed",
  getUrl: (t) => `https://www.reed.co.uk/jobs?keywords=${enc(t)}`,
};

const totaljobs: PlatformLink = {
  name: "Totaljobs",
  getUrl: (t) => `https://www.totaljobs.com/jobs?keywords=${enc(t)}`,
};

const irishJobs: PlatformLink = {
  name: "IrishJobs",
  getUrl: (t) => `https://www.irishjobs.ie/ShowResults.aspx?Keywords=${enc(t)}`,
};

// ── Europe ────────────────────────────────────────────────────────────────────

const stepstone = (tld: string): PlatformLink => ({
  name: "StepStone",
  getUrl: (t) => `https://www.stepstone.${tld}/jobs/?ke=${enc(t)}`,
});

const xing: PlatformLink = {
  name: "Xing",
  getUrl: (t) => `https://www.xing.com/jobs/search?keywords=${enc(t)}`,
};

const welcomeJungle: PlatformLink = {
  name: "Welcome to the Jungle",
  getUrl: (t) => `https://www.welcometothejungle.com/jobs?query=${enc(t)}`,
};

const infoJobs = (tld: string): PlatformLink => ({
  name: "InfoJobs",
  getUrl: (t) => `https://www.infojobs.${tld}/jobsearch/search-results/list.xhtml?keyword=${enc(t)}`,
});

const nationalVacaturebank: PlatformLink = {
  name: "Nationale Vacaturebank",
  getUrl: (t) => `https://www.nationalevacaturebank.nl/vacature/zoeken?query=${enc(t)}`,
};

const karriereAt: PlatformLink = {
  name: "karriere.at",
  getUrl: (t) => `https://www.karriere.at/jobs/${slug(t)}`,
};

const jobsCh: PlatformLink = {
  name: "jobs.ch",
  getUrl: (t) => `https://www.jobs.ch/en/vacancies/?term=${enc(t)}`,
};

const netEmpregos: PlatformLink = {
  name: "Net-Empregos",
  getUrl: (t) => `https://www.net-empregos.com/pesquisa-empregos.asp?q=${enc(t)}`,
};

const finnNo: PlatformLink = {
  name: "Finn.no",
  getUrl: (t) => `https://www.finn.no/job/fulltime/search.html?q=${enc(t)}`,
};

const jobindex: PlatformLink = {
  name: "Jobindex",
  getUrl: (t) => `https://www.jobindex.dk/jobsoegning?q=${enc(t)}`,
};

const pracuj: PlatformLink = {
  name: "Pracuj.pl",
  getUrl: (t) => `https://www.pracuj.pl/praca?q=${enc(t)}`,
};

const jobsCz: PlatformLink = {
  name: "Jobs.cz",
  getUrl: (t) => `https://www.jobs.cz/prace/?q%5B%5D=${enc(t)}`,
};

const ejobs: PlatformLink = {
  name: "eJobs.ro",
  getUrl: (t) => `https://www.ejobs.ro/user/locuri-de-munca/${enc(t)}`,
};

// ── Africa ────────────────────────────────────────────────────────────────────

const pnet: PlatformLink = {
  name: "PNet",
  getUrl: (t) => `https://www.pnet.co.za/jobs.html?criteria=${enc(t)}`,
};

const careerJunction: PlatformLink = {
  name: "CareerJunction",
  getUrl: (t) => `https://www.careerjunction.co.za/jobs/results?Keywords=${enc(t)}`,
};

const jobberman = (country: string): PlatformLink => ({
  name: "Jobberman",
  getUrl: (t) => `https://www.jobberman.com/search?q=${enc(t)}&l=${enc(country)}`,
});

const brighterMonday: PlatformLink = {
  name: "BrighterMonday",
  getUrl: (t) => `https://www.brightermonday.co.ke/listings?q=${enc(t)}`,
};

// ── Latin America ─────────────────────────────────────────────────────────────

const catho: PlatformLink = {
  name: "Catho",
  getUrl: (t) => `https://www.catho.com.br/vagas/${slug(t)}/`,
};

const occMundial: PlatformLink = {
  name: "OCC Mundial",
  getUrl: (t) => `https://www.occ.com.mx/empleos/de-${slug(t)}/`,
};

const computrabajo = (tld: string): PlatformLink => ({
  name: "Computrabajo",
  getUrl: (t) => `https://www.computrabajo.${tld}/ofertas-de-trabajo/?q=${enc(t)}`,
});

// ─────────────────────────────────────────────────────────────────────────────
// Country map — ISO 3166-1 alpha-2 codes → ordered list of platforms
// ─────────────────────────────────────────────────────────────────────────────

export const COUNTRY_PLATFORMS: Record<string, PlatformLink[]> = {

  // ── GCC ────────────────────────────────────────────────────────────────────
  AE: [linkedIn("United Arab Emirates"), bayt("uae"),       gulfTalent("United Arab Emirates"), indeed("ae"), naukriGulf],
  SA: [linkedIn("Saudi Arabia"),         bayt("saudi-arabia"), gulfTalent("Saudi Arabia"),     indeed("sa"), naukriGulf],
  QA: [linkedIn("Qatar"),                bayt("qatar"),     gulfTalent("Qatar"),                indeed("qa"), naukriGulf],
  KW: [linkedIn("Kuwait"),               bayt("kuwait"),    gulfTalent("Kuwait"),               indeed("kw"), naukriGulf],
  BH: [linkedIn("Bahrain"),              bayt("bahrain"),   gulfTalent("Bahrain"),              indeed("bh"), naukriGulf],
  OM: [linkedIn("Oman"),                 bayt("oman"),      gulfTalent("Oman"),                 indeed("om"), naukriGulf],

  // ── Broader Middle East ────────────────────────────────────────────────────
  EG: [linkedIn("Egypt"),   wuzzuf,     bayt("egypt"),   indeed("eg")],
  JO: [linkedIn("Jordan"),  akhtaboot,  bayt("jordan"),  indeed("jo")],
  LB: [linkedIn("Lebanon"), akhtaboot,  bayt("lebanon"), indeed("lb")],
  IQ: [linkedIn("Iraq"),    bayt("iraq"),                indeed("iq")],
  TR: [linkedIn("Turkey"),  kariyer,    indeed("tr"),    glassdoor],

  // ── South Asia ─────────────────────────────────────────────────────────────
  IN: [linkedIn("India"),      naukri, indeed("in"), shine,   glassdoor],
  PK: [linkedIn("Pakistan"),   rozee,  indeed("pk"),  glassdoor],
  BD: [linkedIn("Bangladesh"), bdjobs, indeed("bd")],
  LK: [linkedIn("Sri Lanka"),          indeed("lk"),  glassdoor],
  NP: [linkedIn("Nepal"),              indeed("np"),  glassdoor],

  // ── Southeast Asia ─────────────────────────────────────────────────────────
  SG: [linkedIn("Singapore"),  myCareersFuture, jobStreet("com.sg"), indeed("sg"), glassdoor],
  MY: [linkedIn("Malaysia"),   jobStreet("com.my"),  indeed("my"),  glassdoor],
  PH: [linkedIn("Philippines"),jobStreet("com.ph"),  indeed("ph"),  glassdoor],
  ID: [linkedIn("Indonesia"),  jobStreet("co.id"),   glints, indeed("id")],
  TH: [linkedIn("Thailand"),   jobsDB("th"),          indeed("th"),  glassdoor],
  VN: [linkedIn("Vietnam"),    vietnamWorks,          indeed("vn")],
  HK: [linkedIn("Hong Kong"),  jobsDB("hk"),          indeed("hk"),  glassdoor],

  // ── East Asia ──────────────────────────────────────────────────────────────
  JP: [linkedIn("Japan"),        rikunabi, indeed("jp"), glassdoor],
  KR: [linkedIn("South Korea"),  saramin,  indeed("kr"), glassdoor],
  CN: [linkedIn("China"),        zhaopin,  boss],
  TW: [linkedIn("Taiwan"),       job104,   indeed("tw")],

  // ── Oceania ────────────────────────────────────────────────────────────────
  AU: [linkedIn("Australia"),    seek("com.au"), indeed("au"), glassdoor],
  NZ: [linkedIn("New Zealand"),  seek("co.nz"),  tradeMe,      indeed("nz")],

  // ── North America ──────────────────────────────────────────────────────────
  US: [linkedIn("United States"), indeed("www"), glassdoor, zipRecruiter, monster("com")],
  CA: [linkedIn("Canada"),        indeed("ca"),  glassdoor, jobBank],

  // ── UK & Ireland ───────────────────────────────────────────────────────────
  GB: [linkedIn("United Kingdom"), indeed("uk"), reed, totaljobs, glassdoor],
  IE: [linkedIn("Ireland"),        indeed("ie"), irishJobs, glassdoor],

  // ── Western Europe ─────────────────────────────────────────────────────────
  DE: [linkedIn("Germany"),      indeed("de"), stepstone("de"), xing,          glassdoor],
  AT: [linkedIn("Austria"),      indeed("at"), stepstone("at"), karriereAt,    glassdoor],
  CH: [linkedIn("Switzerland"),  indeed("ch"), jobsCh,          glassdoor],
  FR: [linkedIn("France"),       indeed("fr"), welcomeJungle,   monster("fr"), glassdoor],
  NL: [linkedIn("Netherlands"),  indeed("nl"), nationalVacaturebank, glassdoor],
  BE: [linkedIn("Belgium"),      indeed("be"), stepstone("be"), glassdoor],
  ES: [linkedIn("Spain"),        indeed("es"), infoJobs("net"), glassdoor],
  IT: [linkedIn("Italy"),        indeed("it"), infoJobs("it"),  glassdoor],
  PT: [linkedIn("Portugal"),     indeed("pt"), netEmpregos,     glassdoor],

  // ── Nordics ────────────────────────────────────────────────────────────────
  SE: [linkedIn("Sweden"),  indeed("se"), glassdoor],
  NO: [linkedIn("Norway"),  indeed("no"), finnNo,    glassdoor],
  DK: [linkedIn("Denmark"), indeed("dk"), jobindex,  glassdoor],
  FI: [linkedIn("Finland"), indeed("fi"), glassdoor],

  // ── Eastern Europe ─────────────────────────────────────────────────────────
  PL: [linkedIn("Poland"),         indeed("pl"), pracuj,  glassdoor],
  CZ: [linkedIn("Czech Republic"), indeed("cz"), jobsCz,  glassdoor],
  RO: [linkedIn("Romania"),        indeed("ro"), ejobs,   glassdoor],
  HU: [linkedIn("Hungary"),        indeed("hu"),          glassdoor],
  UA: [linkedIn("Ukraine"),        indeed("ua"),          glassdoor],

  // ── Africa ─────────────────────────────────────────────────────────────────
  ZA: [linkedIn("South Africa"), pnet,                careerJunction, indeed("za")],
  NG: [linkedIn("Nigeria"),      jobberman("nigeria"), indeed("ng")],
  KE: [linkedIn("Kenya"),        brighterMonday,       indeed("ke")],
  GH: [linkedIn("Ghana"),        jobberman("ghana"),   indeed("gh")],

  // ── Latin America ──────────────────────────────────────────────────────────
  BR: [linkedIn("Brazil"),    indeed("br"), catho,       glassdoor],
  MX: [linkedIn("Mexico"),    indeed("mx"), occMundial,  glassdoor],
  AR: [linkedIn("Argentina"), indeed("ar"), computrabajo("com.ar"), glassdoor],
  CO: [linkedIn("Colombia"),  indeed("co"), computrabajo("co"),     glassdoor],
  CL: [linkedIn("Chile"),     indeed("cl"), computrabajo("cl"),     glassdoor],
  PE: [linkedIn("Peru"),      indeed("pe"), computrabajo("pe"),     glassdoor],
};

// Fallback for any country not explicitly listed
export const DEFAULT_PLATFORMS: PlatformLink[] = [
  linkedIn("worldwide"),
  indeed("www"),
  glassdoor,
];

export function getPlatforms(countryCode: string): PlatformLink[] {
  return COUNTRY_PLATFORMS[countryCode.toUpperCase()] ?? DEFAULT_PLATFORMS;
}
